import fabric from '@/lib/fabric';
import { isCropOverlay } from '@/lib/cropTool';
import { isGuideObject } from '@/lib/guideLines';
import { isObjectLocked } from '@/lib/layerOps';
import { isMarqueeOverlay } from '@/lib/marqueeTool';

export const LASSO_KEY = 'craftaraLasso';

export type LassoPt = { x: number; y: number };

export type LassoApplyResult =
  | { mode: 'none'; reason?: string }
  | { mode: 'select'; count: number }
  | { mode: 'cutout' };

/** SVG points attribute from artboard-absolute coordinates. */
export function lassoPointsAttr(points: LassoPt[]): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

export function isLassoOverlay(obj: fabric.Object | null | undefined): boolean {
  return Boolean(obj && (obj as unknown as Record<string, unknown>)[LASSO_KEY]);
}

function pointInPolygon(point: LassoPt, polygon: LassoPt[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function polygonBounds(polygon: LassoPt[]): {
  left: number;
  top: number;
  right: number;
  bottom: number;
} {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const p of polygon) {
    left = Math.min(left, p.x);
    top = Math.min(top, p.y);
    right = Math.max(right, p.x);
    bottom = Math.max(bottom, p.y);
  }
  return { left, top, right, bottom };
}

function rectsIntersect(
  a: { left: number; top: number; width: number; height: number },
  b: { left: number; top: number; right: number; bottom: number }
): boolean {
  return !(
    a.left + a.width < b.left ||
    a.left > b.right ||
    a.top + a.height < b.top ||
    a.top > b.bottom
  );
}

function imageOriginOffset(image: fabric.Image): { ox: number; oy: number } {
  const w = image.width || 0;
  const h = image.height || 0;
  const ox =
    image.originX === 'center' ? w / 2 : image.originX === 'right' ? w : 0;
  const oy =
    image.originY === 'center' ? h / 2 : image.originY === 'bottom' ? h : 0;
  return { ox, oy };
}

/** Map artboard point → image element pixel space (top-left, unscaled). */
function artboardToImageLocal(image: fabric.Image, pt: LassoPt): LassoPt {
  image.setCoords();
  const sx = image.scaleX || 1;
  const sy = image.scaleY || 1;

  // toLocalPoint returns coords in *scaled* visual units from the given origin.
  // Divide by scale to reach Fabric image.width/height pixel space.
  if (typeof image.toLocalPoint === 'function') {
    const local = image.toLocalPoint(new fabric.Point(pt.x, pt.y), 'left', 'top');
    let x = local.x / sx;
    let y = local.y / sy;
    if (image.flipX) x = (image.width || 0) - x;
    if (image.flipY) y = (image.height || 0) - y;
    return { x, y };
  }

  const matrix = image.calcTransformMatrix();
  const inv = fabric.util.invertTransform(matrix);
  const local = fabric.util.transformPoint(new fabric.Point(pt.x, pt.y), inv);
  const { ox, oy } = imageOriginOffset(image);
  return { x: local.x + ox, y: local.y + oy };
}

/** Map image pixel (top-left unscaled) → artboard point. */
function imageLocalToArtboard(
  image: fabric.Image,
  lx: number,
  ly: number
): { x: number; y: number } {
  image.setCoords();
  let x = lx;
  let y = ly;
  if (image.flipX) x = (image.width || 0) - lx;
  if (image.flipY) y = (image.height || 0) - ly;
  const { ox, oy } = imageOriginOffset(image);
  return fabric.util.transformPoint(
    new fabric.Point(x - ox, y - oy),
    image.calcTransformMatrix()
  );
}

export function findImageAtArtboardPoint(
  canvas: fabric.Canvas,
  pt: LassoPt
): fabric.Image | null {
  const objects = canvas.getObjects();
  for (let i = objects.length - 1; i >= 0; i--) {
    const obj = objects[i];
    if (obj.type !== 'image' || !obj.visible) continue;
    if (isCropOverlay(obj) || isMarqueeOverlay(obj) || isLassoOverlay(obj)) continue;
    if (isGuideObject(obj) || obj.excludeFromExport || isObjectLocked(obj)) continue;
    if (typeof obj.containsPoint === 'function') {
      try {
        if (obj.containsPoint(new fabric.Point(pt.x, pt.y), undefined, true, true)) {
          return obj as fabric.Image;
        }
      } catch {
        // fall through to bounds test
      }
    }
    const b = obj.getBoundingRect(true);
    if (
      pt.x >= b.left &&
      pt.x <= b.left + b.width &&
      pt.y >= b.top &&
      pt.y <= b.top + b.height
    ) {
      return obj as fabric.Image;
    }
  }
  return null;
}

/** Topmost unlocked image under the lasso path. */
export function findImageUnderLasso(
  canvas: fabric.Canvas,
  polygon: LassoPt[]
): fabric.Image | null {
  if (polygon.length < 3) return null;

  // Prefer image under the path centroid, then under any vertex
  const pb = polygonBounds(polygon);
  const centroid = {
    x: (pb.left + pb.right) / 2,
    y: (pb.top + pb.bottom) / 2,
  };
  const atCentroid = findImageAtArtboardPoint(canvas, centroid);
  if (atCentroid) return atCentroid;

  for (let i = 0; i < polygon.length; i += Math.max(1, Math.floor(polygon.length / 12))) {
    const hit = findImageAtArtboardPoint(canvas, polygon[i]);
    if (hit) return hit;
  }

  // Fallback: intersecting bounds
  const objects = canvas.getObjects();
  for (let i = objects.length - 1; i >= 0; i--) {
    const obj = objects[i];
    if (obj.type !== 'image' || !obj.visible) continue;
    if (isCropOverlay(obj) || isMarqueeOverlay(obj) || isLassoOverlay(obj)) continue;
    if (isGuideObject(obj) || obj.excludeFromExport || isObjectLocked(obj)) continue;
    const b = obj.getBoundingRect(true);
    if (rectsIntersect(b, pb)) return obj as fabric.Image;
  }
  return null;
}

/** Bounding box of non-transparent pixels (inclusive). */
function opaqueBounds(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  alphaThreshold = 8
): { left: number; top: number; width: number; height: number } | null {
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > alphaThreshold) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return {
    left: minX,
    top: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

/**
 * Keep only the freehand region of an image, then trim to opaque bounds
 * so the object no longer keeps the full original frame size.
 */
export async function cutoutImageWithLasso(
  canvas: fabric.Canvas,
  image: fabric.Image,
  polygon: LassoPt[]
): Promise<boolean> {
  if (polygon.length < 3) return false;

  const el = image.getElement() as HTMLImageElement | HTMLCanvasElement | null;
  if (!el) return false;

  const localPoly = polygon.map((p) => artboardToImageLocal(image, p));
  const w = Math.max(1, Math.round(image.width || 0));
  const h = Math.max(1, Math.round(image.height || 0));
  if (w < 2 || h < 2) return false;

  // Accept polygon if a reasonable share of points land near the image
  const insideCount = localPoly.filter(
    (p) => p.x >= -w * 0.05 && p.y >= -h * 0.05 && p.x <= w * 1.05 && p.y <= h * 1.05
  ).length;
  if (insideCount < 3) {
    console.warn('[lasso] polygon not on image', { insideCount, w, h, sample: localPoly[0] });
    return false;
  }

  const cropX = image.cropX || 0;
  const cropY = image.cropY || 0;

  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return false;

  try {
    ctx.drawImage(el, cropX, cropY, w, h, 0, 0, w, h);
  } catch (err) {
    console.warn('[lasso] drawImage failed (CORS?)', err);
    return false;
  }

  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  ctx.moveTo(localPoly[0].x, localPoly[0].y);
  for (let i = 1; i < localPoly.length; i++) {
    ctx.lineTo(localPoly[i].x, localPoly[i].y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  const bounds = opaqueBounds(ctx, w, h);
  if (!bounds || bounds.width < 1 || bounds.height < 1) {
    console.warn('[lasso] cutout produced empty image — check coordinates');
    return false;
  }

  // Trim to opaque content so selection handles wrap the cutout, not the old frame
  const trimmed = document.createElement('canvas');
  trimmed.width = bounds.width;
  trimmed.height = bounds.height;
  const tctx = trimmed.getContext('2d');
  if (!tctx) return false;
  tctx.drawImage(
    c,
    bounds.left,
    bounds.top,
    bounds.width,
    bounds.height,
    0,
    0,
    bounds.width,
    bounds.height
  );

  const nextUrl = trimmed.toDataURL('image/png');
  const prevScaleX = image.scaleX || 1;
  const prevScaleY = image.scaleY || 1;
  const prevAngle = image.angle || 0;

  // Keep cutout visually in place: artboard pos of the trimmed top-left pixel
  const originArtboard = imageLocalToArtboard(image, bounds.left, bounds.top);

  await new Promise<void>((resolve, reject) => {
    // Never pass crossOrigin for data: URLs — Chromium will fail to decode.
    image.setSrc(nextUrl, () => {
      try {
        image.set({
          cropX: 0,
          cropY: 0,
          left: originArtboard.x,
          top: originArtboard.y,
          scaleX: prevScaleX,
          scaleY: prevScaleY,
          angle: prevAngle,
          originX: 'left',
          originY: 'top',
          dirty: true,
        });
        image.setCoords();
        canvas.setActiveObject(image);
        canvas.requestRenderAll();
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  });

  return true;
}

function objectsWithCenterInLasso(
  canvas: fabric.Canvas,
  polygon: LassoPt[]
): fabric.Object[] {
  return canvas.getObjects().filter((obj) => {
    if (!obj.visible || isCropOverlay(obj) || isMarqueeOverlay(obj)) return false;
    if (isLassoOverlay(obj) || isGuideObject(obj)) return false;
    if (obj.excludeFromExport || isObjectLocked(obj)) return false;
    const b = obj.getBoundingRect(true);
    const center = { x: b.left + b.width / 2, y: b.top + b.height / 2 };
    return pointInPolygon(center, polygon);
  });
}

export function selectObjectsInLasso(
  canvas: fabric.Canvas,
  polygon: LassoPt[],
  options?: { additive?: boolean }
): boolean {
  if (polygon.length < 3) return false;

  const hits = objectsWithCenterInLasso(canvas, polygon);
  const additive = Boolean(options?.additive);
  const prev = additive ? canvas.getActiveObjects().slice() : [];
  const merged = new Map<fabric.Object, fabric.Object>();
  prev.forEach((obj) => merged.set(obj, obj));
  hits.forEach((obj) => merged.set(obj, obj));
  const selected = Array.from(merged.values());

  canvas.discardActiveObject();
  if (!selected.length) {
    canvas.requestRenderAll();
    return false;
  }
  if (selected.length === 1) {
    canvas.setActiveObject(selected[0]);
  } else {
    canvas.setActiveObject(new fabric.ActiveSelection(selected, { canvas }));
  }
  canvas.requestRenderAll();
  return true;
}

/**
 * Smart lasso:
 * - Prefer freehand cutout when the loop is over a photo
 * - Otherwise select objects by center
 */
export async function applyLasso(
  canvas: fabric.Canvas,
  polygon: LassoPt[],
  options?: { additive?: boolean; preferredImage?: fabric.Image | null }
): Promise<LassoApplyResult> {
  if (polygon.length < 3) {
    return { mode: 'none', reason: 'Loop too small — draw a closed shape' };
  }

  const centerHits = objectsWithCenterInLasso(canvas, polygon);
  const nonImageHits = centerHits.filter((o) => o.type !== 'image');
  const imageTarget =
    options?.preferredImage ||
    findImageUnderLasso(canvas, polygon);

  // Multi-shape selection
  if (nonImageHits.length >= 2) {
    const ok = selectObjectsInLasso(canvas, polygon, options);
    return ok
      ? { mode: 'select', count: centerHits.length }
      : { mode: 'none', reason: 'Nothing selected' };
  }

  // Photo cutout (main user expectation)
  if (imageTarget && !options?.additive) {
    try {
      const ok = await cutoutImageWithLasso(canvas, imageTarget, polygon);
      if (ok) return { mode: 'cutout' };
    } catch (err) {
      console.error('[lasso] cutout failed', err);
      return {
        mode: 'none',
        reason: err instanceof Error ? err.message : 'Cutout failed',
      };
    }
  }

  if (centerHits.length >= 1) {
    const ok = selectObjectsInLasso(canvas, polygon, options);
    return ok
      ? { mode: 'select', count: centerHits.length }
      : { mode: 'none', reason: 'Nothing selected' };
  }

  if (imageTarget) {
    try {
      const ok = await cutoutImageWithLasso(canvas, imageTarget, polygon);
      if (ok) return { mode: 'cutout' };
    } catch (err) {
      console.error('[lasso] cutout failed', err);
    }
  }

  return {
    mode: 'none',
    reason: imageTarget
      ? 'Could not cut out that region — try a larger loop on the photo'
      : 'Draw the loop on top of a photo (or around shape centers)',
  };
}
